import React from 'react';
import { cn } from '@/lib/utils';
import { useTraceSidebar } from '@/contexts/TraceSidebarContext';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import type { TraceItem, WaterfallTimings, TraceItemType } from '@/types/trace';
import { formatDuration } from '@/utils/traceTransformer';

const colorMap: Record<TraceItemType, string> = {
  setup: 'bg-slate-500',
  task: 'bg-blue-500',
  tool_call: 'bg-amber-500',
  tool_result: 'bg-green-500',
  observation: 'bg-purple-500',
  thinking: 'bg-pink-500',
  final_output: 'bg-emerald-500',
  synthesis: 'bg-cyan-500',
};

interface WaterfallRowProps {
  item: TraceItem;
  timings: WaterfallTimings;
  zoom: number;
}

export function WaterfallRow({ item, timings, zoom }: WaterfallRowProps) {
  const { selectTrace, isNodeSelected } = useTraceSidebar();
  const isSelected = isNodeSelected(item.id);

  const baseWidth = 400;
  const timelineWidth = baseWidth * zoom;

  // Calculate bar position and width
  const getBarStyle = () => {
    // If no startTime, can't position the bar
    if (item.startTime === null) {
      return { left: '0%', width: '0%' };
    }

    const startOffset = item.startTime - timings.timelineStart;
    const left = (startOffset / timings.totalDuration) * 100;

    // Use duration if available, otherwise use a minimum visible width (1% of timeline)
    const duration = item.duration ?? (timings.totalDuration * 0.01);
    const width = (duration / timings.totalDuration) * 100;

    return {
      left: `${Math.max(0, Math.min(99, left))}%`,
      width: `${Math.max(1, Math.min(100 - left, width))}%`,
    };
  };

  const barStyle = getBarStyle();
  const barColor = colorMap[item.type] || 'bg-gray-500';

  return (
    <TooltipProvider>
      <div
        className={cn(
          'flex items-center border-b cursor-pointer transition-colors',
          'hover:bg-accent',
          isSelected && 'bg-accent'
        )}
        onClick={() => selectTrace(item.id)}
      >
        {/* Label Column */}
        <div
          className="w-48 flex-shrink-0 px-2 py-1.5 text-sm truncate border-r"
          style={{ paddingLeft: `${item.depth * 12 + 8}px` }}
        >
          {item.displayName}
        </div>

        {/* Timeline Bar Column */}
        <div className="relative h-8 flex-1" style={{ minWidth: timelineWidth }}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={cn(
                  'absolute top-1/2 -translate-y-1/2 h-5 rounded-sm',
                  barColor,
                  'flex items-center justify-end px-1 min-w-[2px]'
                )}
                style={barStyle}
              >
                {item.duration !== null && item.duration > 100 && (
                  <span className="text-[10px] text-white font-medium truncate">
                    {formatDuration(item.duration)}
                  </span>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <div className="text-sm">
                <p className="font-medium">{item.displayName}</p>
                <p className="text-muted-foreground">
                  Duration: {formatDuration(item.duration)}
                </p>
                {item.tokenCount.total > 0 && (
                  <p className="text-muted-foreground">
                    Tokens: {item.tokenCount.total}
                  </p>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  );
}

export default WaterfallRow;
